/** Letters group (canonical `S2Lb2uzv`).
 *
 *  Read-receipts are identified by the native Xano `user_id` (the auth/me `id`).
 *  The Letters Inbox passes it so is_read / unread-count / mark-* reflect THIS
 *  user; the backend resolves receipts by it. The job-detail letter drawer
 *  (Brief 06) calls these read-only without it.
 *
 *  Live quirk: GET /list 400s ("Value is less than the minimum value of 1",
 *  param include_read_days) unless include_read_days >= 1 — always send it. */

import { api } from "./client";
import type {
  LetterListResponse,
  LetterListFilters,
  LetterDetail,
  LetterSource,
  LetterUnreadCount,
  LetterMarkAllReadResult,
  LetterDuplicatesResponse,
  LetterMergePreview,
  LetterMergeResult,
} from "@/types";

export function listLetters(filters: LetterListFilters): Promise<LetterListResponse> {
  return api.get<LetterListResponse>("letters", "list", {
    query: {
      application_id: filters.application_id,
      job_id: filters.job_id,
      source: filters.source,
      letter_types: filters.letter_types,
      q: filters.q,
      date_from: filters.date_from,
      date_to: filters.date_to,
      sort_field: filters.sort_field ?? "letter_date",
      sort_direction: filters.sort_direction ?? "desc",
      page: filters.page,
      per_page: filters.per_page,
      user_id: filters.user_id,
      unread_only: filters.unread_only || undefined,
      include_read_days: filters.include_read_days ?? 1, // mandatory >= 1
    },
  });
}

/** GET /get?id=&source= — full single-letter detail incl. parsed raw_json. Pass
 *  the row's source (a BC letter fetched without source returns a planning
 *  record — verified live). user_id is optional (drives is_read). */
export function getLetter(
  id: number,
  source: LetterSource,
  userId?: number,
): Promise<LetterDetail> {
  return api.get<LetterDetail>("letters", "get", {
    query: { id, source, user_id: userId },
  });
}

/** GET /unread-count — badge source (per the user's receipts). Lightweight. */
export function getUnreadCount(userId: number): Promise<LetterUnreadCount> {
  return api.get<LetterUnreadCount>("letters", "unread-count", {
    query: { user_id: userId },
  });
}

/** POST /mark-read — idempotent; `source` MUST come from the row. */
export function markRead(source: LetterSource, letterId: number, userId: number) {
  return api.post<{ was_already_read: boolean }>("letters", "mark-read", {
    body: { source, letter_id: letterId, user_id: userId },
  });
}

/** POST /mark-all-read — receipts every letter this user hasn't read yet, in one
 *  transaction. Idempotent (already-read letters are skipped). `source` narrows
 *  it to one table; nothing else does — the inbox's search/date filters are NOT
 *  honoured, so it clears everything unread within that source. */
export function markAllRead(userId: number, source?: LetterSource) {
  return api.post<LetterMarkAllReadResult>("letters", "mark-all-read", {
    body: { user_id: userId, source },
  });
}

/** POST /mark-unread — deletes the receipt; idempotent. */
export function markUnread(source: LetterSource, letterId: number, userId: number) {
  return api.post<{ deleted: boolean }>("letters", "mark-unread", {
    body: { source, letter_id: letterId, user_id: userId },
  });
}

/* ─── Duplicate review (Review › Letters) ──────────────────────────────────
 * Detect + soft-archive letters that are the same document parsed twice. */

/** GET /duplicates — read-only review queue: exact (`duplicate_letters`) and
 *  possible (`possible_duplicate_letters`) groups within a single table, each
 *  member enriched with its application/job + a pdf link. No params (optional
 *  `source` narrows to one table). */
export function getLetterDuplicates(source?: LetterSource): Promise<LetterDuplicatesResponse> {
  return api.get<LetterDuplicatesResponse>("letters", "duplicates", {
    query: { source },
  });
}

/** POST /merge with dry_run — the no-write preview: who's kept (default = most
 *  recent letter_date, or `keeperId` if given), who's archived, how many
 *  read-receipts repoint, whether a planning task link is inherited. */
export function previewLetterMerge(
  letterIds: number[],
  source: LetterSource,
  keeperId?: number,
): Promise<LetterMergePreview> {
  return api.post<LetterMergePreview>("letters", "merge", {
    body: { letter_ids: letterIds, source, keeper_id: keeperId, dry_run: true },
  });
}

/** POST /merge — commits the merge: repoints the losers' read-receipts onto the
 *  keeper, inherits its planning task link, then soft-archives the losers
 *  (merged_into). Reversible only by clearing merged_into; gate behind the
 *  dry-run preview + a confirm. Re-fetch the queue after (losers drop out). */
export function mergeLetters(
  letterIds: number[],
  source: LetterSource,
  keeperId?: number,
): Promise<LetterMergeResult> {
  return api.post<LetterMergeResult>("letters", "merge", {
    body: { letter_ids: letterIds, source, keeper_id: keeperId },
  });
}
